import { useAuth } from "@/_core/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { getLoginUrl } from "@/const";
import { smartInterval, checkTradingHours } from "@/lib/timeUtils";
import { useIsMobile } from "@/hooks/useMobile";
import {
  LayoutDashboard,
  LogOut, User,
  PanelLeft,
  Search,
  TrendingUp,
  Brain,
  SlidersHorizontal,
  BarChart3,
  Settings,
  Bot,
  Settings2,
  Compass,
  Gem,
  Plug,
  MoreHorizontal,
  Bell,
  Wrench,
  ChevronRight,
  FileCode2,
  PackageCheck,
  Coins,
  Crown,
  Wallet,
  Sparkles, Activity, Zap, Target, Bookmark, ShieldCheck, BellRing, Waves, History } from "lucide-react";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from "./DashboardLayoutSkeleton";
import { Button } from "./ui/button";
import { trpc } from "@/lib/trpc";

// 顶部主菜单项
const topMenuItems = [
  { icon: Search, label: "个股分析", path: "/" },
  { icon: Sparkles, label: "AI 信号", path: "/ai-signals" },
  { icon: Search, label: "T+0 选股", path: "/screening" },
  // [15b 20260815 Steven令] 波段与T+0合并同页: 菜单两项合一, 线切换在页头(两线平级)
  { icon: Bot, label: "自动化交易", path: "/agent" },
  // [20260714 退役 Steven拍板] 极值做T监控菜单下架(signal_monitor同步停推); 页面文件保留两周, /extreme-sim 直链仍可达
  { icon: BellRing, label: "信号推送管理", path: "/extreme-push" },
  // [15b 20260815] 「波段交易」菜单项已并入「自动化交易」(/agent?tab=swing);
  //   /swing-trade 路由保留并重定向, Steven 书签不失效
  { icon: History, label: "T0成绩单", path: "/t0-history" },
  { icon: Target, label: "个股T0画像", path: "/stock-profiles" },
  { icon: ShieldCheck, label: "盈利护盘", path: "/guardian" },
  { icon: Brain, label: "AI复盘 & 调参", path: "/param-analysis" },
  { icon: Compass, label: "短期掘金", path: "/opportunities" },
  { icon: Gem, label: "长期机会", path: "/long-term" },
  { icon: Target, label: "推荐追踪", path: "/recommendations" },
  { icon: Bookmark, label: "我的跟踪", path: "/my-tracking" },
];

// 系统管理子菜单
const systemMenuItems = [
  { icon: Settings, label: "参数配置", path: "/config" },
  { icon: Bot, label: "LLM 管理", path: "/llm" },
  { icon: Settings2, label: "接口配置", path: "/broker" },
  { icon: Bell, label: "通知中心", path: "/notifications" },
  { icon: Plug, label: "系统集成", path: "/integration" },
  { icon: Coins, label: "Token 统计", path: "/token-stats" },
  { icon: FileCode2, label: "掘金策略导出", path: "/emt-export" },
  { icon: PackageCheck, label: "版本管理", path: "/engine-versions" },
  { icon: Activity, label: "主备引擎", path: "/ha" },
  { icon: Zap, label: "L2 稳定性", path: "/l2-stability" },
  { icon: Wrench, label: "引擎维护", path: "/strategy-runs" },
  { icon: MoreHorizontal, label: "更多", path: "/more" },
];

// [Claude 20260522] adminMenuItems 已移除 — 入口迁移到 /me 个人中心 (MeIndex 卡片)

// 所有可导航路径（用于高亮检测）
const allPaths = [
  ...topMenuItems.map(i => i.path),
  ...systemMenuItems.map(i => i.path),
  "/simulation", "/param-analysis", "/backtest", "/portfolio", "/templates",
  "/me/wallet",
];

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 240;
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-10 w-10 text-primary" />
              <h1 className="text-2xl font-bold tracking-tight">AI选股与量化交易系统</h1>
            </div>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              智能选股 · 事件驱动 · 自动交易 · 风险控制
            </p>
          </div>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl();
            }}
            size="lg"
            className="w-full shadow-lg hover:shadow-xl transition-all"
          >
            登录系统
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: DashboardLayoutContentProps) {
  const { user, logout } = useAuth();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // 查询引擎版本状态，判断是否需要发布
  const engineVersionQuery = trpc.engineUpdate.currentWebVersion.useQuery(undefined, {
    refetchInterval: smartInterval(60000, checkTradingHours().isTradingHours, 300000), // 交易时段每分钟，非交易时段5分钟
    enabled: !!user, // 仅登录后查询
  });
  const engineNeedsPublish = engineVersionQuery.data?.isPublished === false;

  // 查找当前活跃菜单标签
  const activeMenuItem = [...topMenuItems, ...systemMenuItems].find(
    (item) => item.path === location
  );

  // 系统管理折叠状态: 当前路由在 systemMenuItems 中时自动展开
  const isInSystemSection = systemMenuItems.some(item => item.path === location);
  const [systemMenuOpen, setSystemMenuOpen] = useState(isInSystemSection);
  useEffect(() => {
    if (isInSystemSection) setSystemMenuOpen(true);
  }, [isInSystemSection]);

  useEffect(() => {
    if (isCollapsed) {
      setIsResizing(false);
    }
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const sidebarLeft =
        sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setSidebarWidth(newWidth);
      }
    };
    const handleMouseUp = () => setIsResizing(false);
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className="border-r-0"
          disableTransition={isResizing}
        >
          <SidebarHeader className="h-14 justify-center">
            <div className="flex items-center gap-3 px-2 transition-all w-full">
              <button
                onClick={toggleSidebar}
                className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none shrink-0"
                aria-label="Toggle navigation"
              >
                <PanelLeft className="h-4 w-4 text-muted-foreground" />
              </button>
              {!isCollapsed ? (
                <div className="flex items-center gap-2 min-w-0">
                  <TrendingUp className="h-5 w-5 text-primary shrink-0" />
                  <span className="font-semibold tracking-tight truncate text-sm">
                    AI选股与量化交易
                  </span>
                  <span className="text-[9px] font-mono text-muted-foreground/60 ml-auto pr-1" title="UI build">
                    b0732
                  </span>
                </div>
              ) : null}
            </div>
          </SidebarHeader>

          <SidebarContent className="gap-0 overflow-y-auto">
            {/* [b0528] 单一 SidebarMenu, 中间用 li 做分隔, 避免两个 SidebarMenu 之间 layout 冲突 */}
            <SidebarMenu className="px-2 py-1">
              {topMenuItems.map((item) => {
                const isActive = location === item.path;
                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      isActive={isActive}
                      onClick={() => setLocation(item.path)}
                      tooltip={item.label}
                      className="h-9 transition-all font-normal text-[13px]"
                    >
                      <item.icon
                        className={`h-4 w-4 ${isActive ? "text-primary" : ""}`}
                      />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}

              {/* 分隔线 (li 内, 同一 ul 内不会 layout 错位) */}
              <li className="list-none mt-2 mb-1 px-3" aria-hidden="true">
                <div className="h-px bg-border" />
              </li>

              {/* 系统管理可折叠标题 (React state, 不用 Radix Collapsible 避免之前的重叠 bug) */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isInSystemSection && !systemMenuOpen}
                  onClick={() => setSystemMenuOpen(o => !o)}
                  tooltip="系统管理"
                  className="h-9 font-normal text-[13px]"
                >
                  <Wrench className={`h-4 w-4 ${isInSystemSection ? "text-primary" : ""}`} />
                  <span>系统管理</span>
                  <ChevronRight
                    className={`ml-auto h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${systemMenuOpen ? "rotate-90" : ""}`}
                  />
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* 折叠内容: 条件渲染 (展开时显示子项, 跟主菜单同级不缩进) */}
              {systemMenuOpen && systemMenuItems.map((item) => {
                const isActive = location === item.path;
                const showBadge = item.path === "/engine-versions" && engineNeedsPublish;
                return (
                  <SidebarMenuItem key={item.path}>
                    <SidebarMenuButton
                      isActive={isActive}
                      onClick={() => setLocation(item.path)}
                      tooltip={item.label}
                      className="h-8 font-normal text-[13px] pl-7"
                    >
                      <item.icon className={`h-3.5 w-3.5 ${isActive ? "text-primary" : ""}`} />
                      <span>{item.label}</span>
                      {showBadge && (
                        <span className="ml-auto flex h-2 w-2 rounded-full bg-red-500 animate-pulse" title="引擎端代码有更新，请发布新版本" />
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>

          </SidebarContent>

          <SidebarFooter className="p-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none">
                  <Avatar className="h-8 w-8 border shrink-0">
                    <AvatarFallback className="text-xs font-medium bg-primary/20 text-primary">
                      {user?.name?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <p className="text-sm font-medium truncate leading-none">
                      {user?.name || "-"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-1">
                      {user?.role === "super_admin" ? "超级管理员"
                       : user?.role === "admin" ? "管理员"
                       : user?.role === "research_t0" ? "T0 研究员"
                       : user?.role === "research" ? "研究员"
                       : "用户"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onSelect={() => setLocation("/me")}
                  className="cursor-pointer"
                >
                  <User className="mr-2 h-4 w-4" />
                  <span>个人中心</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setLocation("/me/wallet")}
                  className="cursor-pointer"
                >
                  <Wallet className="mr-2 h-4 w-4 text-amber-400" />
                  <span>我的钱包</span>
                </DropdownMenuItem>
                {(user?.role === "super_admin" || user?.role === "admin") && (
                  <DropdownMenuItem
                    onSelect={() => setLocation("/me/users")}
                    className="cursor-pointer"
                  >
                    <User className="mr-2 h-4 w-4 text-amber-400" />
                    <span>用户管理</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-1">
                  <span>Web 版本</span>
                  <span className="font-mono">v{import.meta.env.VITE_APP_VERSION || "1.1.0"}</span>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>退出登录</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-12 items-center justify-between bg-background/95 px-2 backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground text-sm">
                {activeMenuItem?.label ?? "菜单"}
              </span>
              {/* [20260812 Steven UX] 移动端 h1 徽章(交易模式/熔断/引擎状态) portal 插槽 */}
              <span id="hdr-badge-slot" className="flex items-center gap-1.5 overflow-x-auto max-w-[70vw]" />
            </div>
          </div>
        )}
        <main className="flex-1 p-4 lg:p-6">{children}</main>
      </SidebarInset>
    </>
  );
}
